import {HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {SigningServiceHttp} from 'app/main/signing-pages/signing-service';
import {QrGenerator, QrGeneratorOptions} from './generateqr';

export interface AuthRequestV5 {
    endUserIp: string;
    personalNumber?: string;
    requirement?: AuthOptionalRequirements;
    user_visible_data?: string;
    userVisibleDataFormat?: "simpleMarkdownV1";
    userNonVisibleData?: string;
    v?:string
}

export interface AuthResponse {
    /**
     * Use in deeplink to start BankId on same device.
     * @example `bankid:///?autostarttoken=[TOKEN]&redirect=[RETURNURL]`
     */
    autoStartToken: string;
    qrStartSecret: string;
    qrStartToken: string;
    orderRef: string;
}

interface AuthOptionalRequirements {
    cardReader?: "class1" | "class2";
    certificatePolicies?: string[];
    issuerCn?: string[];
    autoStartTokenRequired?: boolean;
    allowFingerprint?: boolean;
    personalNumber?:string;
}

//
// Type definitions for /sign
//

export interface SignRequest extends AuthRequestV5 {
    user_visible_data: string;
    endUserIp: string;
}

// API payload interfaces with snake_case properties that support both V5 and V6
export interface AuthPayload {
    endUserIp: string;
    personalNumber?: string;
    requirement?: AuthOptionalRequirements | AuthOptionalRequirementsV6;
    user_visible_data?: string;
    user_non_visible_data?: string;
    user_visible_data_format?: "simpleMarkdownV1";
    v: string;
}

export interface SignPayload extends AuthPayload {
    user_visible_data: string;
}

export interface SignResponse extends AuthResponse { }

//
// Type definitions for /collect
//

export interface CollectRequest {
    orderRef: string;
}

type CollectResponse = CollectResponseV5 | CollectResponseV6;
export interface CollectResponseV5 {
    orderRef: string;
    status: "pending" | "failed" | "complete";
    hintCode?: FailedHintCode | PendingHintCode;
    completionData?: CompletionData;
}

export interface CompletionData {
    user: {
        personalNumber: string;
        name: string;
        givenName: string;
        surname: string;
    };
    device: {
        ipAddress: string;
    };
    cert: {
        notBefore: string;
        notAfter: string;
    };
    signature: string;
    ocspResponse: string;
}

export type FailedHintCode =
    | "expiredTransaction"
    | "certificateErr"
    | "userCancel"
    | "cancelled"
    | "startFailed";

export type PendingHintCode =
    | "outstandingTransaction"
    | "noClient"
    | "started"
    | "userSign";

//
// Type definitions for /cancel
//

export interface CancelRequest extends CollectRequest { }

export interface CancelResponse { }

//
// Type definitions for error responses
//

export interface ErrorResponse {
    errorCode: BankIdErrorCode;
    details: string;
}

export enum BankIdErrorCode {
    ALREADY_IN_PROGRESS = "alreadyInProgress",
    INVALID_PARAMETERS = "invalidParameters",
    UNAUTHORIZED = "unauthorized",
    NOT_FOUND = "notFound",
    METHOD_NOT_ALLOWED = "methodNotAllowed",
    REQUEST_TIMEOUT = "requestTimeout",
    UNSUPPORTED_MEDIA_TYPE = "unsupportedMediaType",
    INTERNAL_ERROR = "internalError",
    MAINTENANCE = "maintenance",
}

export const REQUEST_FAILED_ERROR = "BANKID_NO_RESPONSE";

//
// Collection of overarching types
//

export enum BankIdMethod {
    auth = "auth",
    sign = "sign",
    collect = "collect",
    cancel = "cancel",
}

export type BankIdRequest =
    | AuthRequestV5
    | SignRequest
    | CollectRequest
    | CancelRequest;

export type BankIdResponse =
    | CancelResponse
    | AuthResponse
    | SignResponse
    | CollectResponseV5
    | CollectResponseV6;

//
// Client settings
//

interface BankIdClientSettings {
    production: boolean;
    refreshInterval?: number;
    roleId: number;
    apiRouteName: string
}

//
// Error types
//

export class BankIdError extends Error {
    readonly code: BankIdErrorCode;
    readonly details?: string;

    constructor(code: BankIdErrorCode, details?: string) {
        super(code);
        Error.captureStackTrace(this, this.constructor);

        this.name = "BankIdError";
        this.code = code;
        this.details = details;
    }
}

export class RequestError extends Error {
    readonly request?: HttpErrorResponse;

    constructor(request?) {
        super(REQUEST_FAILED_ERROR);
        Error.captureStackTrace(this, this.constructor);

        this.name = "RequestError";
        this.request = request;
    }
}

@Injectable({
    providedIn: 'root'
})

export class BankIdClientService {
    public options: BankIdClientSettings;
    version = "v5.1";

    constructor(public signingServiceHttp: SigningServiceHttp) {
        return this;
    }

    authenticate(parameters: AuthRequestV5 | AuthRequestV6): Promise<AuthResponse> {
        if (!parameters.endUserIp) {
            throw new Error("Missing required argument endUserIp.");
        }

        // Type guard to check if it's V5 with userVisibleDataFormat
        const hasUserVisibleDataFormat = 'userVisibleDataFormat' in parameters;

        if (
            hasUserVisibleDataFormat &&
            parameters.userVisibleDataFormat != null &&
            parameters.userVisibleDataFormat !== "simpleMarkdownV1"
        ) {
            throw new Error("userVisibleDataFormat can only be simpleMarkdownV1.");
        }

        const payload: AuthPayload = {
            ...parameters,
            user_visible_data: 'user_visible_data' in parameters ? parameters.user_visible_data : undefined,
            user_non_visible_data: 'userNonVisibleData' in parameters ? parameters.userNonVisibleData : undefined,
            user_visible_data_format: hasUserVisibleDataFormat ? parameters.userVisibleDataFormat : undefined,
            v: this.version
        };

        return this.#call<AuthPayload, AuthResponse>(
            BankIdMethod.auth,
            payload,
        );
    }

    sign(parameters: SignRequest | SignRequestV6): Promise<SignResponse> {
        if (!parameters.endUserIp || !parameters.user_visible_data) {
            throw new Error(
                "Missing required arguments: endUserIp, user_visible_data.",
            );
        }

        // Type guard to check if it's V5 with userVisibleDataFormat
        const hasUserVisibleDataFormat = 'userVisibleDataFormat' in parameters;

        if (
            hasUserVisibleDataFormat &&
            parameters.userVisibleDataFormat != null &&
            parameters.userVisibleDataFormat !== "simpleMarkdownV1"
        ) {
            throw new Error("userVisibleDataFormat can only be simpleMarkdownV1.");
        }

        const payload: SignPayload = {
            ...parameters,
            user_visible_data: parameters.user_visible_data,
            user_non_visible_data: 'userNonVisibleData' in parameters ? parameters.userNonVisibleData : undefined,
            user_visible_data_format: hasUserVisibleDataFormat ? parameters.userVisibleDataFormat : undefined,
            v: this.version
        };

        return this.#call<SignPayload, SignResponse>(BankIdMethod.sign, payload);
    }

    collect(parameters: CollectRequest) {
        return this.#call<CollectRequest, CollectResponse>(
            BankIdMethod.collect,
            parameters,
        );
    }

    cancel(parameters: CollectRequest): Promise<CancelResponse> {
        return this.#call<CollectRequest, CancelResponse>(
            BankIdMethod.cancel,
            parameters,
        );
    }

    _awaitPendingCollect(orderRef: string) {
        console.warn("This method has been renamed to 'awaitPendingCollect");
        return this.awaitPendingCollect(orderRef);
    }

    async authenticateAndCollect(
        parameters: AuthRequestV5,
    ): Promise<CollectResponse> {
        const authResponse = await this.authenticate(parameters);
        return this.awaitPendingCollect(authResponse.orderRef);
    }

    async signAndCollect(parameters: SignRequest): Promise<CollectResponse> {
        const signResponse = await this.sign(parameters);

        return this.awaitPendingCollect(signResponse.orderRef);
    }

    awaitPendingCollect(orderRef: string): Promise<CollectResponse> {
        return new Promise((resolve, reject) => {
            const timer = setInterval(() => {
                this.collect({ orderRef })
                    .then(response => {
                        if (response.status === "complete") {
                            clearInterval(timer);
                            resolve(response);
                        } else if (response.status === "failed") {
                            clearInterval(timer);
                            reject(response);
                        }
                    })
                    .catch(error => {
                        clearInterval(timer);
                        reject(error);
                    });
            }, this.options.refreshInterval);
        });
    }

    #call<Req extends BankIdRequest, Res extends BankIdResponse>(
        method: BankIdMethod,
        payload: Req,
    ): Promise<Res> {
        return new Promise((resolve, reject) => {
            const apiRouteName = this.options.apiRouteName || `bank-id`;
            this.signingServiceHttp.post(`${apiRouteName}/${this.options.roleId}/${method}`, {...payload, v: this.version}).subscribe({
                next: (response) => {
                    const res = response.response_body || response;
                    if( Object.prototype.hasOwnProperty.call(res, 'errorCode')){
                        let thrownError = response;
                        thrownError = new BankIdError(
                            res.errorCode,
                            res.details,
                        );
                        reject(thrownError);
                    }else{
                        resolve(res);
                    }
                },
                error: (error) => {
                    let thrownError = error;
                    thrownError = new RequestError(error);
                    reject(thrownError);
                }
            })
        });
    }
}

interface AuthOptionalRequirementsV6 {
  pinCode?: boolean;
  cardReader?: "class1" | "class2";
  mrtd?: boolean;
  certificatePolicies?: string[];
  personalNumber: string;
}

export interface AuthRequestV6 {
  endUserIp: string;
  requirement?: AuthOptionalRequirementsV6;
}

export interface SignRequestV6 extends AuthRequestV6 {
  user_visible_data: string;
  userVisibleDataFormat?: "simpleMarkdownV1";
  userNonVisibleData?: string;
}

interface AuthResponseV6 extends AuthResponse {
  qr?: QrGenerator;
}

interface SignResponseV6 extends SignResponse {
  qr?: QrGenerator;
}

export interface CompletionDataV6 {
  user: {
    personalNumber: string;
    name: string;
    givenName: string;
    surname: string;
  };
  device: {
    ipAddress: string;
    uhi?: string;
  };
  /** ISO 8601 date format YYYY-MM-DD with a UTC time zone offset. */
  bankIdIssueDate: string;
  stepUp: boolean;
  signature: string;
  ocspResponse: string;
}

export interface CollectResponseV6
  extends Omit<CollectResponseV5, "completionData"> {
  completionData?: CompletionDataV6;
}

interface BankIdClientSettingsV6 extends BankIdClientSettings {
  /** Controls whether to attach an instance of {@link QrGenerator} to BankID responses  */
  qrEnabled?: boolean;
  qrOptions?: QrGeneratorOptions;
  v?: string;
}

/**
 * A class for creating a BankId Client based on v6.0 api, extending from BankIdClient
 * @see https://www.bankid.com/en/utvecklare/guider/teknisk-integrationsguide/webbservice-api
 */

@Injectable({
    providedIn: 'root'
})
export class BankIdClientV6 extends BankIdClientService {
  version = "v6.0";
  public options: BankIdClientSettingsV6;

  constructor(public signingServiceHttp: SigningServiceHttp) {
    super(signingServiceHttp);
    this.options = {
      ...(this.options as BankIdClientSettings),
      qrEnabled: this.options?.qrEnabled ?? true,
      qrOptions: this.options?.qrOptions ?? QrGenerator.defaultOptions,
      v:this.version
    };
  }

  async authenticate(parameters: AuthRequestV6): Promise<AuthResponseV6> {
    const resp = await super.authenticate(parameters);
    const qr = this.options.qrEnabled
      ? new QrGenerator(resp, this.options.qrOptions)
      : undefined;
    return { ...resp, qr };
  }

  async sign(parameters: SignRequestV6): Promise<SignResponseV6> {
    const resp = await super.sign(parameters);
    const qr = this.options.qrEnabled
      ? new QrGenerator(resp, this.options.qrOptions)
      : undefined;
    return { ...resp, qr };
  }

  async collect(parameters: CollectRequest) {
    return super.collect(parameters) as Promise<CollectResponseV6>;
  }
}
